<template>
    <Modal size="xxl" @close="$emit('close')">
        <template #header>
            <div
                data-slot="dialog-header"
                class="flex flex-col gap-2 text-center sm:text-left"
            >
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-4">
                        <div
                            class="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-6 w-6 text-blue-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <div>
                            <h2
                                id="radix-:r4:"
                                data-slot="dialog-title"
                                class="mb-2 text-xl font-semibold text-gray-900"
                            >
                                {{ $t("vue.dmarc_report_details") }}
                            </h2>
                            <p
                                id="radix-:r5:"
                                data-slot="dialog-description"
                                class="text-sm text-gray-600"
                            >
                                {{ $t("vue.email_authentication_analysis") }}
                                {{ ip }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #body>
            <div class="p-8">
                <div class="mb-12 grid grid-cols-1 gap-8 md:grid-cols-4">
                    <div class="text-center">
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-server h-6 w-6 text-gray-600"
                                aria-hidden="true"
                            >
                                <rect
                                    width="20"
                                    height="8"
                                    x="2"
                                    y="2"
                                    rx="2"
                                    ry="2"
                                ></rect>
                                <rect
                                    width="20"
                                    height="8"
                                    x="2"
                                    y="14"
                                    rx="2"
                                    ry="2"
                                ></rect>
                                <line x1="6" x2="6.01" y1="6" y2="6"></line>
                                <line x1="6" x2="6.01" y1="18" y2="18"></line>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.ip_address") }}
                        </p>
                        <p class="font-mono font-medium text-gray-900">
                            {{ ip }}
                        </p>
                    </div>
                    <div class="text-center">
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-mail h-6 w-6 text-gray-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"
                                ></path>
                                <rect
                                    x="2"
                                    y="4"
                                    width="20"
                                    height="16"
                                    rx="2"
                                ></rect>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.emails_reported") }}
                        </p>
                        <p class="font-medium text-gray-900">
                            {{
                                new Intl.NumberFormat().format(
                                    detailRow.fail + detailRow.pass,
                                )
                            }}
                        </p>
                    </div>
                    <div class="text-center">
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-globe h-6 w-6 text-gray-600"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <path
                                    d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                ></path>
                                <path d="M2 12h20"></path>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.header_from") }}
                        </p>
                        <p class="font-medium text-gray-900">
                            {{ detailRow.identifiers.header_from }}
                        </p>
                    </div>
                    <div v-if="detailRow.fail > 0" class="text-center">
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-red-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-6 w-6 text-red-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.dmarc_status") }}
                        </p>
                        <p class="font-medium capitalize text-red-700">
                            {{ $t("vue.fail") }}
                        </p>
                    </div>
                    <div v-else class="text-center">
                        <div
                            class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-green-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-6 w-6 text-green-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <p class="mb-1 text-sm text-gray-600">
                            {{ $t("vue.dmarc_status") }}
                        </p>
                        <p class="font-medium capitalize text-green-700">
                            {{ $t("vue.pass") }}
                        </p>
                    </div>
                </div>
                <!--ENVELOPE FROM & TO-->
                <div class="mb-12">
                    <h3 class="mb-6 text-lg font-medium text-gray-900">
                        {{ $t("vue.domain_information") }}
                    </h3>
                    <div class="mb-12 grid grid-cols-1 gap-2 md:grid-cols-2">
                        <!-- ENVELOPE FROM -->
                        <div
                            v-if="
                                detailRow.identifiers.envelope_from &&
                                detailRow.identifiers.envelope_from !== '<>'
                            "
                            class="rounded-lg bg-gray-50 p-6"
                        >
                            <div class="mb-2 flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-globe h-5 w-5 text-gray-500"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path
                                        d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                    ></path>
                                    <path d="M2 12h20"></path>
                                </svg>
                                <span class="font-medium text-gray-700">{{
                                    $t("vue.envelope_from")
                                }}</span>
                            </div>
                            <p class="font-mono text-gray-900">
                                {{ detailRow.identifiers.envelope_from }}
                            </p>
                        </div>
                        <div v-else class="rounded-lg bg-gray-50 p-6">
                            <div class="mb-2 flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-globe h-5 w-5 text-gray-500"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path
                                        d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                    ></path>
                                    <path d="M2 12h20"></path>
                                </svg>
                                <span class="font-medium text-gray-700">{{
                                    $t("vue.envelope_from")
                                }}</span>
                            </div>
                            <p class="font-mono text-gray-900">
                                envelope_from data label not available
                            </p>
                        </div>
                        <!-- ENVELOPE TO -->
                        <div
                            v-if="
                                detailRow.identifiers.envelope_to &&
                                detailRow.identifiers.envelope_to !== '<>'
                            "
                            class="rounded-lg bg-gray-50 p-6"
                        >
                            <div class="mb-2 flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-mail h-5 w-5 text-gray-500"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"
                                    ></path>
                                    <rect
                                        x="2"
                                        y="4"
                                        width="20"
                                        height="16"
                                        rx="2"
                                    ></rect>
                                </svg>
                                <span class="font-medium text-gray-700">{{
                                    $t("vue.envelope_to")
                                }}</span>
                            </div>
                            <p class="font-mono text-gray-900">
                                {{ detailRow.identifiers.envelope_to }}
                            </p>
                        </div>
                        <div v-else class="rounded-lg bg-gray-50 p-6">
                            <div class="mb-2 flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-globe h-5 w-5 text-gray-500"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path
                                        d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                    ></path>
                                    <path d="M2 12h20"></path>
                                </svg>
                                <span class="font-medium text-gray-700">{{
                                    $t("vue.envelope_to")
                                }}</span>
                            </div>
                            <p class="font-mono text-gray-900">
                                envelope_to data label not available
                            </p>
                        </div>
                    </div>
                </div>
                <div>
                    <h3 class="mb-6 text-lg font-medium text-gray-900">
                        {{ $t("vue.authentication_results") }}
                    </h3>
                    <div class="space-y-6">
                        <div class="rounded-lg border border-gray-200 p-6">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-shield h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                        ></path>
                                    </svg>
                                    <div>
                                        <h4 class="font-medium text-gray-900">
                                            {{ $t("vue.dmarc_authentication") }}
                                        </h4>
                                        <p class="text-sm text-gray-600">
                                            {{
                                                $t(
                                                    "vue.domain_based_message_authentication",
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>
                                <div
                                    v-if="detailRow.fail > 0"
                                    class="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-circle-x h-4 w-4"
                                        aria-hidden="true"
                                    >
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="m15 9-6 6"></path>
                                        <path d="m9 9 6 6"></path>
                                    </svg>
                                    <span>{{ $t("vue.fail_status") }}</span>
                                </div>
                                <div
                                    v-else
                                    class="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-circle-check-big h-4 w-4"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M21.801 10A10 10 0 1 1 17 3.335"
                                        ></path>
                                        <path d="m9 11 3 3L22 4"></path>
                                    </svg>
                                    <span>{{ $t("vue.pass_status") }}</span>
                                </div>
                            </div>
                        </div>

                        <!--SPF-->
                        <div class="rounded-lg border border-gray-200 p-6">
                            <div class="mb-4 flex items-center justify-between">
                                <div class="flex items-center gap-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-shield-check h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                        ></path>
                                        <path d="m9 12 2 2 4-4"></path>
                                    </svg>
                                    <div>
                                        <h4 class="font-medium text-gray-900">
                                            SPF Authentication
                                        </h4>
                                        <p class="text-sm text-gray-600">
                                            DomainKeys Identified Mail signature
                                            verification and alignment
                                        </p>
                                    </div>
                                </div>

                                <!--SPF PASS-->
                                <div class="flex items-center gap-3">
                                    <div
                                        v-if="
                                            props.detailRow.auth_results.spf
                                                .result === 'pass'
                                        "
                                        class="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <span>Pass</span>
                                    </div>
                                    <div
                                        v-else
                                        class="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-x h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path d="m15 9-6 6"></path>
                                            <path d="m9 9 6 6"></path>
                                        </svg>
                                        <span>Fail</span>
                                    </div>
                                    <!--SPF ALIGN-->
                                    <div
                                        v-if="
                                            props.detailRow.auth_results.spf
                                                .domain ===
                                            props.detailRow.identifiers
                                                .header_from
                                        "
                                        class="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <span>Aligned</span>
                                    </div>
                                    <div
                                        v-else
                                        class="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-x h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path d="m15 9-6 6"></path>
                                            <path d="m9 9 6 6"></path>
                                        </svg>
                                        <span>Not aligned</span>
                                    </div>
                                </div>
                            </div>

                            <!--SPF DOMAINS-->
                            <div class="pl-9">
                                <div class="rounded-lg bg-gray-50 p-3">
                                    <div class="mb-2 flex items-center gap-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-globe h-4 w-4 text-gray-500"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path
                                                d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                            ></path>
                                            <path d="M2 12h20"></path></svg
                                        ><span
                                            class="text-sm font-medium text-gray-700"
                                            >SPF Domain</span
                                        >
                                    </div>

                                    <div
                                        v-if="
                                            Array.isArray(
                                                detailRow.auth_results.spf,
                                            ) === true
                                        "
                                        class="space-y-1"
                                    >
                                        <p
                                            v-for="spfResult in detailRow
                                                .auth_results.spf"
                                            :key="spfResult"
                                            class="font-mono text-sm text-gray-900"
                                        >
                                            {{ spfResult.domain }}
                                        </p>
                                    </div>
                                    <div
                                        v-else-if="
                                            detailRow.auth_results.spf &&
                                            typeof detailRow.auth_results
                                                .spf === 'object' &&
                                            detailRow.auth_results.spf
                                                .domain !== ''
                                        "
                                        class="space-y-1"
                                    >
                                        <p
                                            class="font-mono text-sm text-gray-900"
                                        >
                                            {{
                                                detailRow.auth_results.spf
                                                    .domain
                                            }}
                                        </p>
                                    </div>
                                    <div v-else class="space-y-1">
                                        <p
                                            class="font-mono text-sm text-gray-900"
                                        >
                                            spf domain name not available
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!--DKIM-->
                        <div class="rounded-lg border border-gray-200 p-6">
                            <div class="mb-4 flex items-center justify-between">
                                <div class="flex items-center gap-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-shield-check h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                        ></path>
                                        <path d="m9 12 2 2 4-4"></path>
                                    </svg>
                                    <div>
                                        <h4 class="font-medium text-gray-900">
                                            DKIM Authentication
                                        </h4>
                                        <p class="text-sm text-gray-600">
                                            DomainKeys Identified Mail signature
                                            verification and alignment
                                        </p>
                                    </div>
                                </div>

                                <!--DKIM PASS-->
                                <div class="flex items-center gap-3">
                                    <div
                                        v-if="dkimResult.result === 'pass'"
                                        class="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <span>Pass</span>
                                    </div>
                                    <div
                                        v-else-if="
                                            dkimResult.result === 'neutral'
                                        "
                                        class="inline-flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm font-medium text-yellow-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <span>Neutral</span>
                                    </div>
                                    <div
                                        v-else
                                        class="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-x h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path d="m15 9-6 6"></path>
                                            <path d="m9 9 6 6"></path>
                                        </svg>
                                        <span>Fail</span>
                                    </div>

                                    <!--DKIM ALIGN-->
                                    <div
                                        v-if="dkimResult.aligned === true"
                                        class="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <span>Aligned</span>
                                    </div>
                                    <div
                                        v-else
                                        class="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-x h-4 w-4"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path d="m15 9-6 6"></path>
                                            <path d="m9 9 6 6"></path>
                                        </svg>
                                        <span>Not aligned</span>
                                    </div>
                                </div>
                            </div>

                            <!--DKIM DOMAINS-->
                            <div class="pl-9">
                                <div class="rounded-lg bg-gray-50 p-3">
                                    <div class="mb-2 flex items-center gap-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-globe h-4 w-4 text-gray-500"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path
                                                d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                            ></path>
                                            <path d="M2 12h20"></path></svg
                                        ><span
                                            class="text-sm font-medium text-gray-700"
                                            >DKIM Domains</span
                                        >
                                    </div>
                                    <div
                                        v-if="dkimDomains.length > 0"
                                        class="space-y-1"
                                    >
                                        <p
                                            v-for="domain in dkimDomains"
                                            :key="domain"
                                            class="font-mono text-sm text-gray-900"
                                        >
                                            {{ domain }}
                                        </p>
                                    </div>
                                    <div v-else>
                                        <p
                                            class="font-mono text-sm text-gray-900"
                                        >
                                            dkim domain name not available
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="mt-12 rounded-lg border border-blue-200 bg-blue-50 p-6"
                >
                    <div class="flex items-start gap-4">
                        <div
                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-shield h-5 w-5 text-blue-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                ></path>
                            </svg>
                        </div>
                        <div>
                            <h4 class="mb-2 font-medium text-gray-900">
                                {{ $t("vue.security_assessment") }}
                            </h4>
                            <p v-if="detailRow.fail > 0" class="text-gray-700">
                                {{
                                    $t("vue.dmarc_authentication_checks_failed")
                                }}
                            </p>
                            <p v-else class="text - gray - 700">
                                {{
                                    $t("vue.dmarc_authentication_checks_passed")
                                }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton
                    size="md"
                    color="yellow"
                    data-cy="create-token-finish"
                    @click="$emit('close')"
                >
                    {{ $t("vue.close") }}
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import Modal from "@/components/Atomic/Modals/Modal.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { defineProps, defineEmits } from "vue";

const props = defineProps<{
    detailRow: {
        identifiers: {
            header_from: string;
            envelope_from: string;
        };
        auth_results: {
            spf: {
                result: string;
                domain: string;
            };
            dkim: {
                result: string;
                domain: string;
            };
        };
    };
    ip: {
        type: string;
        default: () => "";
    };
    reportedDomain: {
        type: string;
        default: () => "";
    };
}>();

defineEmits(["close"]);

const dkimResult = computed(() => {
    if (
        props.detailRow.auth_results.dkim &&
        props.detailRow.auth_results.dkim.constructor === Array
    ) {
        let result;
        let aligned = false;

        let resultFailed = false;
        let resultNeutral = false;

        let alignedFailed = false;

        for (const row of props.detailRow.auth_results.dkim) {
            result = row.result;
            if (row.result === "fail" || row.result === "permerror") {
                resultFailed = true;
            }
            if (row.result === "neutral") {
                resultNeutral = true;
            }
            if (row.domain === props.detailRow.identifiers.header_from) {
                aligned = true;
            } else {
                alignedFailed = true;
            }
        }

        if (resultFailed === true) {
            result = "fail";
        }
        if (resultNeutral === true) {
            result = "neutral";
        }

        if (alignedFailed === true) {
            aligned = false;
        }

        return {
            result: result,
            aligned: aligned,
        };
    } else {
        return {
            result:
                props.detailRow.auth_results.dkim !== undefined
                    ? props.detailRow.auth_results.dkim.result
                    : "fail",
            aligned:
                props.detailRow.auth_results.dkim !== undefined &&
                props.detailRow.auth_results.dkim.domain ===
                    props.detailRow.identifiers.header_from,
        };
    }
});

const dkimDomains = computed(() => {
    let domains = [];

    if (
        props.detailRow.auth_results.dkim &&
        props.detailRow.auth_results.dkim.constructor === Array
    ) {
        for (const row of props.detailRow.auth_results.dkim) {
            domains.push(row.domain);
        }
    } else {
        if (props.detailRow.auth_results.dkim) {
            domains.push(props.detailRow.auth_results.dkim.domain);
        }
    }

    return domains;
});
</script>
